import { NodeType } from "@/components/devtools/point-cloud-wireframe";
import { selectIElementIdToColorMap } from "@/data-preparation-tool/store/data-preparation-ui/data-preparation-ui-selectors";
import { useAppSelector } from "@/store/store-hooks";
import {
  Dropdown,
  DropdownOption,
  FaroSwitch,
  FaroText,
  grey,
  useIsDarkScheme,
} from "@faro-lotv/flat-ui";
import { validateEnumValue } from "@faro-lotv/foundation";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useMemo, type JSX } from "react";
import {
  Scatter,
  ScatterChart,
  ScatterShapeProps,
  Tooltip,
  TooltipContentProps,
  XAxis,
  YAxis,
} from "recharts";
import { DebugOverlaySection } from "./debug-overlay-section";
import { useLodVisualizationSettings } from "./lod-visualization-settings";
import { useRenderingStatsContext } from "./rendering-stats-context";

/** @returns visualization of the point cloud nodes which are being rendered. */
export function RenderedNodesView(): JSX.Element | null {
  return (
    <DebugOverlaySection title="Rendered Nodes">
      <RenderingVisualization />
    </DebugOverlaySection>
  );
}

function RenderingVisualization(): JSX.Element {
  const isDarkMode = useIsDarkScheme();
  const colorMap = useAppSelector(selectIElementIdToColorMap(isDarkMode));
  const { pointCloudStatsMap } = useRenderingStatsContext();

  const renderingData = useMemo(
    () =>
      Object.entries(pointCloudStatsMap).map(([id, stats]) => ({
        rendered: stats.gpu.nodes,
        depth: stats.depth.nodes,
        color: colorMap[id],
      })),
    [pointCloudStatsMap, colorMap],
  );

  const maxRendered = useMemo(
    () => Math.max(...renderingData.map((data) => data.rendered)),
    [renderingData],
  );

  const maxDepth = useMemo(
    () => Math.max(5, ...renderingData.map((data) => data.depth)),
    [renderingData],
  );

  const { isEnabled, nodeType, setIsEnabled, setNodeType } =
    useLodVisualizationSettings();

  const nodeTypeOptions: DropdownOption[] = useMemo(
    () =>
      Object.values(NodeType).map((nodeType) => ({
        key: nodeType,
        label: nodeType,
        value: nodeType,
      })),
    [],
  );

  return (
    <Stack sx={{ justifyContent: "center" }}>
      <ScatterChart
        width={300}
        height={150}
        data={renderingData}
        margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
      >
        <XAxis
          dataKey="rendered"
          type="number"
          label={{ value: "rendered", style: { fill: grey[400] } }}
          stroke={grey[400]}
          domain={[0, maxRendered + 2]}
          ticks={[0, maxRendered]}
        />
        <YAxis
          dataKey="depth"
          type="number"
          label={{
            value: "depth",
            angle: -90,
            style: { fill: grey[400] },
          }}
          stroke={grey[400]}
          width={30}
          domain={[0, maxDepth + 1]}
          ticks={[0, maxDepth]}
        />
        <Scatter name="rendered nodes" shape={ScatterPoint} />
        <Tooltip content={CustomTooltip} />
      </ScatterChart>

      <Stack>
        <FaroSwitch
          dark
          checked={isEnabled}
          onToggle={() => setIsEnabled(!isEnabled)}
          label="LOD Visualization"
        />
        <Dropdown
          dark
          disabled={!isEnabled}
          value={nodeType}
          options={nodeTypeOptions}
          onChange={(event) => {
            const { value } = event.target;
            if (validateEnumValue(value, NodeType)) {
              setNodeType(value);
            }
          }}
        />
      </Stack>
    </Stack>
  );
}

function ScatterPoint({
  cx,
  cy,
  payload,
}: Omit<ScatterShapeProps, "payload"> & {
  payload?: { color: string };
}): JSX.Element | null {
  if (cx === undefined || cy === undefined) return null;

  return (
    <circle cx={cx} cy={cy} r={2} fill={payload?.color} stroke={grey[900]} />
  );
}

function CustomTooltip({
  active,
  payload,
}: TooltipContentProps): JSX.Element | null {
  if (!active || payload.length === 0) {
    return null;
  }

  const { rendered, depth } = payload[0].payload;

  return (
    <Box
      component="div"
      sx={{
        background: grey[1000],
        padding: 1,
        borderRadius: 2,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{ justifyContent: "space-between" }}
      >
        <FaroText variant="bodyS" dark>
          nodes rendered
        </FaroText>
        <FaroText variant="bodyS" dark>
          {rendered}
        </FaroText>
      </Stack>
      <Stack
        direction="row"
        spacing={1}
        sx={{ justifyContent: "space-between" }}
      >
        <FaroText variant="bodyS" dark>
          node depth
        </FaroText>
        <FaroText variant="bodyS" dark>
          {depth}
        </FaroText>
      </Stack>
    </Box>
  );
}
